import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { concat, delay, map, Observable, of, switchMap, toArray } from 'rxjs';
import { environment } from 'src/environments/environment';
import { IAccount, IMailboxSyncResult, MailboxType, AccountSyncStatus, AccountConnectionStatus } from '../models/account.models';
import { APIResponse } from 'src/app/core/models/api-response';
import { skipErrorToast } from 'src/app/core/http/error-toast.token';

/**
 * Mailboxes per bulk-sync request. Mirrors the backend's `SYNC_BULK_MAX_MAILBOXES`
 * (`smart_inbox_sync_bulk_max_mailboxes`, default 50) — over it the DTO rejects the whole
 * request with a 400, so the client chunks rather than letting one oversized tenant get a
 * blanket failure.
 */
const SYNC_BULK_MAX_MAILBOXES = 50;

/**
 * Handles mailbox/account CRUD operations and the connection wizard flow.
 */
@Injectable({ providedIn: 'root' })
export class AccountService {

  // ── Public properties ──────────────────────────────────────────────────────

  /** When true, methods return mock data instead of calling the API. */
  useMockData = environment.useMockData ?? false;

  // ── Private properties ─────────────────────────────────────────────────────

  private baseUrl = environment.BASE_URL_API + 'inbox';

  // ── Constructor ────────────────────────────────────────────────────────────

  constructor(private http: HttpClient) {}

  // ── Account CRUD ──────────────────────────────────────────────────────────

  listAllAccounts(): Observable<APIResponse<IAccount>> {
    if (this.useMockData) {
      const accounts: IAccount[] = [
        {
          id: 1,
          name: 'Inbox 1',
          email: 'inbox1@example.com',
          provider: 'gmail',
          mailbox_type: 'personal',
          mailbox_owner_id: 6,
          connection_status: AccountConnectionStatus.CONNECTED,
          sync_status: AccountSyncStatus.IDLE,
          last_sync_at: new Date(Date.now() - 1000 * 60 * 60 * 24 * 30).toISOString(),
          last_sync_error: null,
          total_threads: 0,
          unread_count: 0,
          created_at: new Date(Date.now() - 1000 * 60 * 60 * 24 * 30).toISOString(),
          default_signature_new: null,
          default_signature_reply_forward: null,
        },
        {
          id: 2,
          name: 'Inbox 2',
          email: 'inbox2@example.com',
          provider: 'gmail',
          mailbox_type: 'personal',
          mailbox_owner_id: 1,
          connection_status: AccountConnectionStatus.CONNECTED,
          sync_status: AccountSyncStatus.IDLE,
          last_sync_at: new Date(Date.now() - 1000 * 60 * 60 * 19).toISOString(),
          last_sync_error: null,
          total_threads: 0,
          unread_count: 0,
          created_at: new Date(Date.now() - 1000 * 60 * 60 * 24 * 30).toISOString(),
          default_signature_new: null,
          default_signature_reply_forward: null,
        },
      ];

      return of<APIResponse<IAccount>>({
        data: accounts,
        object: undefined,
        message: 'Success',
        metadata: { page: 1, limit: 10, pages: 1, count: accounts.length },
      });
    }
    return this.http.get<any>(`${this.baseUrl}/mailboxes`);
  }

  updateAccount(inboxId: number, payload: { name?: string; mailbox_type?: MailboxType }): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}/mailboxes/${inboxId}`, payload);
  }

  /**
   * Queues a sync for the given mailboxes. Answers `202` with one row per mailbox — refused
   * ones included — so callers must read `data` rather than treat the `202` as "all synced".
   * `503` means nothing could be queued. The effect owns the error message, hence
   * `skipErrorToast` (the interceptor would otherwise toast on top of it).
   *
   * Chunked at the route's own cap: past `SYNC_BULK_MAX_MAILBOXES` the DTO rejects the whole
   * request with a 400, so a tenant above that size would get a generic "could not sync"
   * for every mailbox including the ones that were fine. The chunks' rows are concatenated
   * back into one response, so callers still see one row per requested mailbox.
   */
  refreshAccountsConnection(accountsIds: number[]): Observable<APIResponse<IMailboxSyncResult>> {
    if (this.useMockData) {
      return of<APIResponse<IMailboxSyncResult>>({
        data: accountsIds.map((mailbox_id) => ({ mailbox_id, queued: true })),
        object: undefined,
        message: 'Success',
        metadata: { page: 1, limit: accountsIds.length, pages: 1, count: accountsIds.length },
      }).pipe(delay(1000));
    }

    if (accountsIds.length <= SYNC_BULK_MAX_MAILBOXES) {
      return this.postMailboxSync(accountsIds);
    }

    const chunks: number[][] = [];
    for (let i = 0; i < accountsIds.length; i += SYNC_BULK_MAX_MAILBOXES) {
      chunks.push(accountsIds.slice(i, i + SYNC_BULK_MAX_MAILBOXES));
    }

    // Sequential, not parallel: each chunk claims mailboxes and fills the shared background
    // queue, and firing them at once is how one tenant starves every other pool user.
    // A chunk that errors (e.g. a 503) fails the whole call, which is the behaviour the
    // effect's error branch already handles.
    return concat(...chunks.map((ids) => this.postMailboxSync(ids))).pipe(
      toArray(),
      map((responses) => ({
        ...responses[responses.length - 1],
        data: responses.flatMap((response) => response.data ?? []),
      })),
    );
  }

  private postMailboxSync(mailboxIds: number[]): Observable<APIResponse<IMailboxSyncResult>> {
    return this.http.post<APIResponse<IMailboxSyncResult>>(`${this.baseUrl}/mailboxes/sync`, {
      mailbox_ids: mailboxIds,
    }, { context: skipErrorToast() });
  }

  /**
   * Disconnects WITHOUT deleting: conversations stay and the account stays listed as
   * `disconnected`, ready to re-connect. Idempotent.
   *
   * Used to call `DELETE /mailboxes/{id}` — which is why "Disconnect" destroyed every
   * conversation. `deleteAccount` is only for the explicit "Delete connection" choice.
   */
  disconnectAccount(inboxId: number): Observable<any> {
    if (this.useMockData) {
      return of(null).pipe(delay(1000), switchMap(() => of({ message: 'Success' })));
    }
    return this.http.post<any>(`${this.baseUrl}/mailboxes/${inboxId}/disconnect`, {});
  }

  /**
   * PERMANENT: cascades every thread, message, task, comment and draft. Only for the
   * "Delete connection" branch — the single place that owns the destructive URL.
   */
  deleteAccount(inboxId: number): Observable<any> {
    if (this.useMockData) {
      return of(null).pipe(delay(1000), switchMap(() => of({ message: 'Success' })));
    }
    return this.http.delete<any>(`${this.baseUrl}/mailboxes/${inboxId}`);
  }

  // ── Connection wizard ─────────────────────────────────────────────────────

  initializeConnectionWizard(): Observable<any> {
    const origin = window.location.origin;
    return this.http.post<any>(`${this.baseUrl}/mailboxes/hosted-auth-link`, {
      success_redirect_url: `${origin}/unipile-callback?success=true`,
      failure_redirect_url: `${origin}/unipile-callback?success=false`,
    });
  }

  /**
   * Opens the connection wizard in a popup and returns an observable that emits
   * true if the connection succeeded, false if it failed or was cancelled.
   */
  openConnectionWizard(): Observable<boolean> {
    return this.initializeConnectionWizard().pipe(
      switchMap((response) => {
        const url = response?.object?.url;
        if (!url) {
          console.warn('Connection wizard did not return a URL');
          return of(false);
        }

        const width = 1024;
        const height = 800;
        const left = (screen.width - width) / 2;
        const top = (screen.height - height) / 2;
        const popup = window.open(
          url,
          'connectionWizard',
          `width=${width},height=${height},left=${left},top=${top},resizable=no,scrollbars=no`,
        );

        if (!popup) {
          console.warn('Popup blocked or failed to open');
          return of(false);
        }

        return new Observable<boolean>((subscriber) => {
          const messageListener = (event: MessageEvent) => {
            if (event.origin !== window.location.origin) return;
            if (event.data?.type !== 'UNIPILE_DONE') return;

            window.removeEventListener('message', messageListener);
            setTimeout(() => {
              try {
                if (popup && !popup.closed) popup.close();
              } catch (err) {
                console.warn('Error closing popup (likely from extension cleanup):', err);
              }
            }, 100);

            const success = !!event.data?.success;
            subscriber.next(success);
            subscriber.complete();
          };

          window.addEventListener('message', messageListener);

          return () => {
            window.removeEventListener('message', messageListener);
            try {
              if (popup && !popup.closed) popup.close();
            } catch (err) {
              console.warn('Error closing popup:', err);
            }
          };
        });
      }),
    );
  }
}
